import { c as _c } from "react/compiler-runtime";
import { FallbackSuspense } from '@newfront-insurance/admin-ui/layout';
import { GeneralAppProviders } from '@newfront-insurance/app-providers';
import { isNotUndefined } from '@newfront-insurance/core';
import { initializeDatadogLogsAndRUM } from '@newfront-insurance/dd-rum';
import { registerLocale } from 'i18n-iso-countries';
import enLang from 'i18n-iso-countries/langs/en.json';
import type { AppProps } from 'next/app';
import { Suspense, lazy, useEffect, useState, type JSX } from 'react';
import { HeadTitle } from '@/client/components/head-title';
import { Providers } from '@/client/providers';
import { AuthProvider } from '@/client/providers/auth';
import { TRPCProvider } from '@/client/providers/trpc';
import { getConfig } from '@/config';
import '@newfront-insurance/tailwind-config/globals.css';
import '../client/global.css';
registerLocale(enLang);
initializeDatadogLogsAndRUM('ACCOUNTS');
const config = getConfig();
const ReactQueryDevtoolsProduction = lazy(() => import('@tanstack/react-query-devtools/production').then(d => ({
  default: d.ReactQueryDevtools
})));

// Extend the Window interface to include toggleDevtools
declare global {
  interface Window {
    toggleDevtools: () => void;
  }
}

/**
 * The component will wrap every page that is rendered. This component stays alive for the entire session. This should
 * be used to initialize providers that are used by the entire application..
 */
export default function App(props) {
  const $ = _c(10);
  const {
    Component
  } = props;
  const [showDevtools, setShowDevtools] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (isNotUndefined(window)) {
        window.toggleDevtools = () => setShowDevtools(_temp);
      }
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <HeadTitle title="Loading..." />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== Component) {
    t3 = <Component />;
    $[3] = Component;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== showDevtools) {
    t4 = config.REACT_QUERY.ENABLE_DEVTOOLS && showDevtools && <Suspense fallback={null}><ReactQueryDevtoolsProduction styleNonce="react-query-devtools-production" /></Suspense>;
    $[5] = showDevtools;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <GeneralAppProviders authProvider={AuthProvider} config={config} trpcProvider={TRPCProvider} useConditionalAuthProvider={false} analyticsAppName="accounts"><Providers>{t2}<FallbackSuspense>{t3}{t4}</FallbackSuspense></Providers></GeneralAppProviders>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function _temp(old) {
  return !old;
}